// The ONE client shell for the main app's tokened public pages: hosted forms
// and intake, booking, e-sign, uploads and document requests, and the
// marketing links. Built to docs/design/client-design-standard.md (rule 1:
// "One shell") and its drawing; the client app at my.stewardship.pro has the
// same shell in its own build (web/client/src/App.tsx, lane 2.1).
//
// CONSUMER: anonymous public web. Nothing here reads a session or an API.
//
// DECISION: a component with props, not a CSS class pages assemble by hand.
// The drift the design program measured came from every page hand-building
// its frame; a page that renders <ClientShell> cannot get the hero, the card
// overlap or the footer wrong.

import { type ReactNode, useEffect, useState } from "react";

import {
  type FormBrand,
  STEWARDSHIP_BRAND,
  displayPhone,
  telHref,
} from "@shared/forms/form-branding";
import { OFFICE_EMAIL, PRIVACY_POLICY_URL } from "@shared/forms/office-contact";
import { OFFICE_ADDRESS } from "@shared/scheduling/office-location";

import "./client-shell.css";

export interface ClientShellProps {
  /** The screen's heading: the task (standard rule 3), never a welcome. */
  title: ReactNode;
  /** The line under it: why, and how long. */
  lead?: ReactNode | undefined;
  eyebrow?: ReactNode | undefined;
  /** Something drawn in the hero above the eyebrow (booking's step dots). */
  heroTop?: ReactNode | undefined;
  /** Something drawn in the hero under the lead (booking's back link). */
  heroBottom?: ReactNode | undefined;
  /** Whose page this is (the form's brand); Stewardship when absent. */
  brand?: FormBrand | undefined;
  /** A long body (a form, a document): the card column widens on a computer. */
  wide?: boolean | undefined;
  /** Inside an embed iframe the host page is the chrome; only the cards render. */
  embedded?: boolean | undefined;
  /** Fixed-position layers (a toast, a dialog), outside the page's flow. */
  overlay?: ReactNode | undefined;
  /** An extra class on the page root, so a family's own stylesheet can
   *  scope its values to it (booking: "book-public"). */
  className?: string | undefined;
  /** Marks the card column busy for assistive tech while it loads. */
  busy?: boolean | undefined;
  children: ReactNode;
}

export function ClientShell({
  title,
  lead,
  eyebrow,
  heroTop,
  heroBottom,
  brand = STEWARDSHIP_BRAND,
  wide = false,
  embedded = false,
  overlay,
  busy = false,
  className,
  children,
}: ClientShellProps) {
  if (embedded) {
    return (
      <div className={["cs cs--embedded", className].filter(Boolean).join(" ")}>
        <main className="cs-stack" aria-busy={busy || undefined}>
          {children}
        </main>
        {overlay}
      </div>
    );
  }
  const phone = displayPhone(brand.phone);
  return (
    <>
      <div className={["cs", wide ? "cs--wide" : "", className ?? ""].filter(Boolean).join(" ")}>
        <a className="cs-skip" href="#cs-main">
          Skip to the page
        </a>
        <header className="cs-hero">
          <div className="cs-topbar">
            <div className="cs-brand">
              <img className="cs-logo" src="/stewardship-logo.png" alt="" width={36} height={36} />
              <span className="cs-wordmark">{brand.wordmark}</span>
            </div>
          </div>
          <div className="cs-hero-copy">
            {heroTop}
            {eyebrow && <div className="cs-eyebrow">{eyebrow}</div>}
            <h1>{title}</h1>
            {lead && <div className="cs-lead">{lead}</div>}
            {heroBottom}
          </div>
          <div className="cs-promise">
            <span>
              Questions? <a href={telHref(brand.phone)}>{phone}</a>
            </span>
          </div>
        </header>
        <div className="cs-side">
          <main id="cs-main" className="cs-stack" aria-busy={busy || undefined}>
            {children}
          </main>
          <footer className="cs-legal">
            Questions? <a href={telHref(brand.phone)}>{phone}</a> &middot;{" "}
            <a href={`mailto:${OFFICE_EMAIL}`}>{OFFICE_EMAIL}</a>
            <br />
            {brand.tagline ?? brand.wordmark} &middot; {OFFICE_ADDRESS}
            <br />
            <a href={PRIVACY_POLICY_URL}>Privacy policy</a>
          </footer>
        </div>
      </div>
      {overlay}
    </>
  );
}

/** A button in the shell's look, with the busy rule built in: disabled while
 *  busy, the same width, the "-ing" label, a spinner (standard rule 5). */
export function ShellButton({
  children,
  busyLabel,
  busy = false,
  primary = false,
  disabled = false,
  onClick,
  type = "button",
}: {
  children: ReactNode;
  busyLabel?: ReactNode;
  busy?: boolean;
  primary?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
}) {
  const cls = ["cs-btn", primary ? "cs-btn--pri" : "", busy ? "cs-btn--busy" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button type={type} className={cls} disabled={busy || disabled} onClick={onClick}>
      {busy && <span className="cs-spin" aria-hidden="true" />}
      {busy ? (busyLabel ?? children) : children}
    </button>
  );
}

const ICON_PATHS = {
  check: "m5 12.5 4.5 4.5L19 7.5",
  alert: "M12 8v5M12 16h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18",
  link: "M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1",
  phone:
    "M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2",
} as const;

export function ShellIcon({ name, small = false }: { name: keyof typeof ICON_PATHS; small?: boolean }) {
  return (
    <svg
      className={small ? "cs-ic cs-ic--s" : "cs-ic"}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

/** The loading card: a skeleton the shape of the card, and after 4 seconds
 *  the standard's "Still loading" line. No clock turns slow into failed.
 *  DECISION: the drawing's action is "Cancel"; a public page has nothing to
 *  cancel back to, so the action here is "Try again" (a reload), which is
 *  what a person stuck on a slow link actually wants. */
export function ShellLoadingCard() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 4000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="cs-card" role="status" aria-label="Loading">
      <div className="cs-skl cs-skl--w" />
      <div className="cs-skl" style={{ width: "60%" }} />
      <div className="cs-saved" style={{ justifyContent: "space-between", minHeight: 44 }}>
        <span>{slow ? "Still loading. Taking longer than usual." : ""}</span>
        {slow && (
          <button type="button" className="cs-link" onClick={() => window.location.reload()}>
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
